import { useState } from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { useMarketplaceGenies } from '@/hooks/agent/useAgents';
import { useBulkUpdateDocuments } from '@/hooks/documents/useDocumentMutations';
import { useAllFolders } from '@/hooks/folders/useFolders';
import { useProfiles } from '@/hooks/auth/useProfiles';
import { DocumentWithMetadata } from '@/types/knowledge/knowledge-base';

interface BulkUpdateDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  selectedDocuments: DocumentWithMetadata[];
}

export function BulkUpdateDrawer({ isOpen, onClose, selectedDocuments }: BulkUpdateDrawerProps) {
  const bulkUpdate = useBulkUpdateDocuments();
  const { data: folders = [], isLoading: isLoadingFolders } = useAllFolders();
  const [updates, setUpdates] = useState<Record<string, any>>({});
  const [selectedFields, setSelectedFields] = useState<Set<string>>(new Set());
  const { data: marketplaceGenies = [], isLoading: isLoadingGenies } = useMarketplaceGenies(selectedFields.has("consumer_genie_id"));
  const { data: profiles = [], isLoading: isLoadingProfiles } = useProfiles();

  const toggleField = (field: string) => {
    const newSelected = new Set(selectedFields);
    if (newSelected.has(field)) {
      newSelected.delete(field);
      const newUpdates = { ...updates };
      delete newUpdates[field];
      setUpdates(newUpdates);
    } else {
      newSelected.add(field);
    }
    setSelectedFields(newSelected);
  };

  const handleValueChange = (field: string, value: any) => {
    setUpdates((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async () => {
    if (selectedDocuments.length === 0) return;

    const documentIds = selectedDocuments.map((doc) => doc.id);
    const finalUpdates = Object.fromEntries(Object.entries(updates).filter(([key]) => selectedFields.has(key)));

    await bulkUpdate.mutateAsync({
      documentIds,
      updates: finalUpdates,
    });

    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      <div className="absolute inset-0 bg-black/50 transition-opacity" onClick={onClose} />
      <div className="absolute right-0 top-0 h-full w-full max-w-xl bg-white shadow-xl flex flex-col">
        <div className="flex items-center justify-between border-b p-6">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Bulk Update Documents</h2>
            <p className="text-sm text-gray-600 mt-1">{selectedDocuments.length} document(s) selected</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* is_active */}
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <Checkbox id="is_active" checked={selectedFields.has("is_active")} onCheckedChange={() => toggleField("is_active")} />
              <label htmlFor="is_active" className="text-sm font-medium text-gray-700 cursor-pointer">
                Activity Status
              </label>
            </div>
            {selectedFields.has("is_active") && (
              <div className="pl-6">
                <Select
                  value={updates.is_active ? "active" : "inactive"}
                  onValueChange={(value) => handleValueChange("is_active", value === "active")}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {/* is_public */}
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <Checkbox id="is_public" checked={selectedFields.has("is_public")} onCheckedChange={() => toggleField("is_public")} />
              <label htmlFor="is_public" className="text-sm font-medium text-gray-700 cursor-pointer">
                Privacy Status
              </label>
            </div>
            {selectedFields.has("is_public") && (
              <div className="pl-6">
                <Select
                  value={updates.is_public ? "public" : "private"}
                  onValueChange={(value) => handleValueChange("is_public", value === "public")}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="public">Public</SelectItem>
                    <SelectItem value="private">Private</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {/* is_private */}
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <Checkbox id="is_private" checked={selectedFields.has("is_private")} onCheckedChange={() => toggleField("is_private")} />
              <label htmlFor="is_private" className="text-sm font-medium text-gray-700 cursor-pointer">
                Is Private
              </label>
            </div>
            {selectedFields.has("is_private") && (
              <div className="pl-6">
                <Select
                  value={updates.is_private ? "yes" : "no"}
                  onValueChange={(value) => handleValueChange("is_private", value === "yes")}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="yes">Yes</SelectItem>
                    <SelectItem value="no">No</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {/* status */}
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <Checkbox id="status" checked={selectedFields.has("status")} onCheckedChange={() => toggleField("status")} />
              <label htmlFor="status" className="text-sm font-medium text-gray-700 cursor-pointer">
                Processing Status
              </label>
            </div>
            {selectedFields.has("status") && (
              <div className="pl-6">
                <Select value={updates.status || ""} onValueChange={(value) => handleValueChange("status", value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="failed">Failed</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {/* source_type */}
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <Checkbox id="source_type" checked={selectedFields.has("source_type")} onCheckedChange={() => toggleField("source_type")} />
              <label htmlFor="source_type" className="text-sm font-medium text-gray-700 cursor-pointer">
                Source Type
              </label>
            </div>
            {selectedFields.has("source_type") && (
              <div className="pl-6">
                <Select value={updates.source_type || ""} onValueChange={(value) => handleValueChange("source_type", value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select source type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="audio_transcription">Audio Transcription</SelectItem>
                    <SelectItem value="video_transcription">Video Transcription</SelectItem>
                    <SelectItem value="pdf_upload">PDF Upload</SelectItem>
                    <SelectItem value="document_upload">Document Upload</SelectItem>
                    <SelectItem value="website">Website</SelectItem>
                    <SelectItem value="website-scrape">Website Scrape</SelectItem>
                    <SelectItem value="youtube">YouTube</SelectItem>
                    <SelectItem value="notes">Notes</SelectItem>
                    <SelectItem value="simple-research">Simple Research</SelectItem>
                    <SelectItem value="deep-research">Deep Research</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {/* folder_id */}
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <Checkbox id="folder_id" checked={selectedFields.has("folder_id")} onCheckedChange={() => toggleField("folder_id")} />
              <label htmlFor="folder_id" className="text-sm font-medium text-gray-700 cursor-pointer">
                Folder
              </label>
            </div>
            {selectedFields.has("folder_id") && (
              <div className="pl-6">
                <Select
                  value={updates.folder_id === null ? "__none__" : updates.folder_id || ""}
                  onValueChange={(value) => handleValueChange("folder_id", value === "__none__" ? null : value)}
                  disabled={isLoadingFolders}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={isLoadingFolders ? "Loading folders..." : "Select a folder"} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">No folder</SelectItem>
                    {folders.map((folder) => (
                      <SelectItem key={folder.id} value={folder.id}>
                        {folder.name}
                        {folder.owner?.email && ` (${folder.owner.email})`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {/* consumer_genie_id */}
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <Checkbox
                id="consumer_genie_id"
                checked={selectedFields.has("consumer_genie_id")}
                onCheckedChange={() => toggleField("consumer_genie_id")}
              />
              <label htmlFor="consumer_genie_id" className="text-sm font-medium text-gray-700 cursor-pointer">
                Consumer Genie
              </label>
            </div>
            {selectedFields.has("consumer_genie_id") && (
              <div className="pl-6">
                <Select
                  value={updates.consumer_genie_id === null ? "__none__" : updates.consumer_genie_id || ""}
                  onValueChange={(value) => handleValueChange("consumer_genie_id", value === "__none__" ? null : value)}
                  disabled={isLoadingGenies}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={isLoadingGenies ? "Loading genies..." : "Select a genie"} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">None</SelectItem>
                    {marketplaceGenies.map((genie: any) => (
                      <SelectItem key={genie.id} value={genie.id}>
                        {genie.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          {/* user_id */}
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <Checkbox id="user_id" checked={selectedFields.has("user_id")} onCheckedChange={() => toggleField("user_id")} />
              <label htmlFor="user_id" className="text-sm font-medium text-gray-700 cursor-pointer">
                Reassign Owner
              </label>
            </div>
            {selectedFields.has("user_id") && (
              <div className="pl-6">
                <Select
                  value={updates.user_id || ""}
                  onValueChange={(value) => handleValueChange("user_id", value)}
                  disabled={isLoadingProfiles}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={isLoadingProfiles ? "Loading users..." : "Select a user"} />
                  </SelectTrigger>
                  <SelectContent>
                    {[...profiles]
                      .sort((a, b) => (a.full_name || a.email || "").localeCompare(b.full_name || b.email || ""))
                      .map((profile) => (
                        <SelectItem key={profile.id} value={profile.id}>
                          {profile.full_name && profile.email ? (
                            <span className="flex w-full items-center justify-between gap-3">
                              <span>{profile.full_name}</span>
                              <span className="text-gray-500">{profile.email}</span>
                            </span>
                          ) : (
                            profile.full_name || profile.email
                          )}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </div>

        <div className="border-t p-6 flex gap-3 justify-end">
          <Button variant="outline" onClick={onClose} disabled={bulkUpdate.isPending}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={
              selectedFields.size === 0 ||
              selectedDocuments.length === 0 ||
              bulkUpdate.isPending ||
              (selectedFields.has("user_id") && !updates.user_id)
            }
          >
            {bulkUpdate.isPending ? "Updating..." : `Update ${selectedDocuments.length} Document(s)`}
          </Button>
        </div>
      </div>
    </div>
  );
}
